import {
  ApplicationConfig,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withNavigationErrorHandler,
} from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideTransloco } from '@jsverse/transloco';
import { registerLocaleData } from '@angular/common';
import localeRo from '@angular/common/locales/ro';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { routes } from './app.routes';
import { MOCK_DATA_PROVIDERS } from './core/providers/mock-data.providers';
import { TranslocoHttpLoader } from './core/loaders/transloco.loader';
import { AuthConfigService } from './core/config/auth-config.service';
import { LanguageService } from './core/services/language.service';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { SLA_THRESHOLDS } from './core/tokens/sla-thresholds.token';
import { AuthService } from './core/services/auth.service';
import { STAFF_ROLES } from './core/constants/staff-roles';
import { NavigationFailureService } from './core/services/navigation-failure.service';

registerLocaleData(localeRo);

/**
 * Roles whose screens render SLA chips — the portal-side mirror of `SlaThresholdResource`'s
 * staff-only `@PreAuthorize`. Keep the two in step: a role added there without being added here
 * silently loses its SLA colouring; the reverse only puts a 403 in that role's console.
 *
 * This is the shared internal-staff role list (`core/constants/staff-roles`), NOT a private copy —
 * the notifications inbox derives its chip set from the same array (Rule 17).
 */
const SLA_LADDER_ROLES = STAFF_ROLES;

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      // EM-1634 — without this, a lazy route whose chunk 404s abandons the navigation SILENTLY:
      // the user clicks Open and the app simply does not move. Runs in an injection context.
      withNavigationErrorHandler(event => {
        inject(NavigationFailureService).recordNavigationError(event.error, event.url);
      }),
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideOAuthClient(),
    provideAppInitializer(() => {
      // Eagerly construct LanguageService at bootstrap so its initial
      // `transloco.setActiveLang(...)` fires HERE — before any component view
      // exists — rather than lazily on first injection. If it were constructed
      // lazily by the shared UserAccountMenuComponent inside a page's
      // `*transloco` embedded view, that synchronous lang change would force the
      // enclosing `*transloco` to re-create its view mid-firstCreatePass and
      // crash the page ("Reached the max number of directives").
      inject(LanguageService);

      // Injected HERE, before the await below: `inject()` only works inside the injection context,
      // which is lost once the promise continuation runs.
      const slaThresholds = inject(SLA_THRESHOLDS);
      const auth = inject(AuthService);

      const authConfig = inject(AuthConfigService);
      authConfig.configure();

      return authConfig.initialize().then((authenticated) => {
        // Load the platform SLA ladder ONCE, app-wide, fire-and-forget.
        //
        // `deriveSlaClass` is a pure function reading a module-level variable, so it is only correct
        // once something has fetched the windows. Leaving that to each page meant only the two officer
        // workspaces did it: `kyc/financing-review`, `kyc/auction-review` and
        // `underwriting/disbursement-intervention` all colour SLA chips and none of them loaded, so
        // deep-linking or refreshing straight into one of those rendered a task minutes from breach as
        // permanently green. Doing it here fixes those three and every page added later.
        //
        // AFTER `initialize()`, not before it: the fetch used to sit above `authConfig.configure()`,
        // so it left before OAuth was configured and carried no bearer token. `GET /api/platform/
        // sla-thresholds` answered 401 on EVERY load, `applySlaThresholds` never ran from here, and
        // those three pages stayed permanently green — the very defect this call was added to fix,
        // while looking correct in the source.
        //
        // Gated on a STAFF role because the endpoint is `@PreAuthorize` staff-only
        // (UNDERWRITER / KYC_OFFICER / CUSTOMER_SUPPORT / SENIOR_MANAGER / MASTER_ADMIN). An SME
        // borrower, lender or E-D has no SLA chips anywhere, so firing it for them only trades the
        // 401 for a 403 in every customer's console.
        //
        // NOT awaited: bootstrap must not block on it, and `deriveSlaClass` already degrades safely
        // (breached vs not) until the values arrive. `load()` caches and de-dupes, so the workspaces'
        // own calls remain harmless no-ops.
        if (authenticated && auth.hasAnyRole([...SLA_LADDER_ROLES])) {
          slaThresholds.load().subscribe({ error: () => undefined });
        }
        return authenticated;
      });
    }),
    provideTransloco({
      config: {
        availableLangs: ['ro', 'en'],
        defaultLang: 'ro',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    ...MOCK_DATA_PROVIDERS,
  ],
};
